import type { GenericObject } from '@makemydeal/dr-platform-types';
import { FINANCE, FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';
import {
    LOADING_FLEX_BUY_PAYMENT_GRID,
    LOADING_UPDATE_FLEX_BUY_CASH_DOWN,
    LOADING_UPDATE_FLEX_BUY_TERM_AND_PERCENT,
    UPDATE_FLEX_BUY_PAYMENT,
    UPDATE_FLEX_BUY_PAYMENT_GRID_DATA,
    UPDATE_FLEX_BUY_TERMS_GRID,
    UPDATE_FLEX_BUY_PAYMENT_GRID_AXIS_VALUES,
    UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE,
    RESET_FLEX_BUY_PAYMENT_GRID_DATA,
    SET_FLEX_BUY_PAYMENT_GRID_PRICED_AT,
    REVERT_FFB_DEAL_TO_BASE_FINANCE
} from '../types/actionTypes';
import { addFlexBuyPaymentGridMeta } from '../utils/paymentGridUtils';
import type { UpdateTermGridOptions } from './creators';
import type { FlexBuyPaymentGridAxisValues, UpdateFlexBuyPaymentGridDataPayload } from '../types/flexBuyTypes';
import type { AddFlexBuyPaymentGridMetaOptions } from '../types/commonTypes';
import { PAYMENT_REQUEST_OVERRIDE_NODE } from '../constants/payment';
import type { AnyDispatch } from '@makemydeal/dr-browser-utils/typedefs/types/reduxTypes';

export const flexBuyTermList = [FlexBuyAvailableMonths.MONTH_66, FlexBuyAvailableMonths.MONTH_75];

export const updateFlexBuyPaymentPercent = (
    newPercentValue: number,
    options?: {
        meta?: GenericObject<unknown>;
        requestPayment?: boolean;
        paymentGridOptions?: AddFlexBuyPaymentGridMetaOptions;
    }
) => {
    const action = {
        type: UPDATE_FLEX_BUY_PAYMENT,
        payload: [newPercentValue],
        meta: {
            ...options?.meta,
            originalAction: { meta: { middleware: { offerType: FINANCE } } },
            middleware: {
                payment: options?.requestPayment ?? true
            }
        }
    };
    return addFlexBuyPaymentGridMeta(action, options?.paymentGridOptions);
};

export const updateFlexBuyPaymentGridData = (
    payload: UpdateFlexBuyPaymentGridDataPayload,
    options?: {
        meta?: GenericObject<unknown>;
    }
) => {
    return {
        type: UPDATE_FLEX_BUY_PAYMENT_GRID_DATA,
        payload,
        meta: {
            ...options?.meta
        }
    };
};

export const loadingUpdateFlexBuyTermPercent = ({ columnIndex }: { columnIndex: number }) => {
    return {
        type: LOADING_UPDATE_FLEX_BUY_TERM_AND_PERCENT,
        payload: {
            columnIndex
        }
    };
};

export const loadingUpdateFlexBuyCashDown = ({ rowId }: { rowId: number }) => {
    return {
        type: LOADING_UPDATE_FLEX_BUY_CASH_DOWN,
        payload: {
            rowId
        }
    };
};

export const loadingFlexBuyPaymentGrid = ({
    flexBuyDownPayments: flexBuyDownPayments,
    flexBuyPercents,
    flexBuyTerms
}: FlexBuyPaymentGridAxisValues) => {
    return {
        type: LOADING_FLEX_BUY_PAYMENT_GRID,
        payload: {
            termList: flexBuyTermList,
            flexBuyPercents,
            flexBuyDownPayments,
            flexBuyTerms
        }
    };
};

export function updateFlexBuyPaymentGridAxisValues(params: FlexBuyPaymentGridAxisValues) {
    const { flexBuyDownPayments: flexBuyDownPayments, flexBuyPercents, flexBuyTerms } = params;
    return {
        type: UPDATE_FLEX_BUY_PAYMENT_GRID_AXIS_VALUES,
        payload: {
            termList: flexBuyTermList,
            flexBuyPercents,
            flexBuyDownPayments,
            flexBuyTerms
        }
    };
}

export function updateFlexBuyTermsGrid(params: FlexBuyPaymentGridAxisValues, options?: UpdateTermGridOptions & GenericObject) {
    const { flexBuyDownPayments, flexBuyPercents, flexBuyTerms } = params;

    const action = {
        type: UPDATE_FLEX_BUY_TERMS_GRID,
        meta: {
            middleware: {
                analytics: {
                    eventName: UPDATE_FLEX_BUY_TERMS_GRID
                },
                payment: true,
                ...(options?.nonSelectedTermHonoringSV && { nonSelectedTermHonoringSV: options.nonSelectedTermHonoringSV }),
                ...options
            },
            [PAYMENT_REQUEST_OVERRIDE_NODE]: {
                finance: {
                    termList: flexBuyTermList,
                    flexBuyDownPayments,
                    flexBuyPercents,
                    flexBuyTerms
                }
            }
        }
    };

    return addFlexBuyPaymentGridMeta(action, { isMainPayment: options?.isMainPayment || false });
}

export function updateFlexBuyPaymentGridToggle(toggled: boolean, userIntent?: string) {
    return {
        payload: toggled,
        type: UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE,
        ...(userIntent && { meta: { userIntent } })
    };
}

/**
 * US2041815: records when the flex buy grid priced, so a stored grid can be aged.
 *
 * The timestamp is taken where the response lands rather than at save time — a save-time stamp would
 * make a weeks-old quote look freshly priced and the freshness rule could never fire.
 */
export const setFlexBuyPaymentGridPricedAt = (pricedAt: number) => {
    return {
        type: SET_FLEX_BUY_PAYMENT_GRID_PRICED_AT,
        payload: pricedAt
    };
};

export const resetFlexBuyPaymentGridData = () => {
    return {
        type: RESET_FLEX_BUY_PAYMENT_GRID_DATA
    };
};

export const revertFFBDealToBaseFinance = () => (dispatch: AnyDispatch) => {
    return dispatch({ type: REVERT_FFB_DEAL_TO_BASE_FINANCE, meta: { middleware: { payment: true } } });
};
